import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { useResponsive } from '../hooks/useResponsive';
import { track } from '../lib/analytics';
import { getApiUrl } from '../config';

const STORAGE_KEY = 'hasSeenWelcomeWeb_v1';

type Step = 'bifurcation' | 'create-group' | 'share-group' | 'paste-link';

export const WelcomeModal: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>('bifurcation');
  const [groupName, setGroupName] = useState('');
  const [createdGroupId, setCreatedGroupId] = useState<number | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [pasteInput, setPasteInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        setOpen(true);
        track('onboarding_bifurcation_viewed', {});
      }
    } catch {
      /* localStorage no disponible: no mostramos */
    }
  }, []);

  const finish = (reason: 'completed' | 'skipped') => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
    track(reason === 'completed' ? 'onboarding_completed' : 'onboarding_skipped', { step });
    setOpen(false);
  };

  const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(getApiUrl('/api/groups'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name: groupName.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Error al crear el grupo');
      }
      const groupData = await res.json();
      const groupId: number = groupData.id || groupData.group?.id;
      setCreatedGroupId(groupId);

      // Get invite link
      const inviteRes = await fetch(getApiUrl(`/api/groups/${groupId}/invite-link`), {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      let token = '';
      if (inviteRes.ok) {
        const inviteData = await inviteRes.json();
        token = inviteData.token || inviteData.inviteToken || '';
      }
      const url = token
        ? `https://futbolero.ar/join-group/${token}`
        : `https://futbolero.ar/groups/${groupId}`;
      setInviteUrl(url);

      track('onboarding_group_created', { groupId });
      setStep('share-group');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al crear el grupo');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = () => {
    if (!inviteUrl) return;
    const msg = `¡Sumate a mi grupo en Futbolero! ${inviteUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
    track('onboarding_whatsapp_shared', { groupId: createdGroupId });
  };

  const handleCopyLink = () => {
    if (!inviteUrl) return;
    navigator.clipboard.writeText(inviteUrl).catch(() => {});
    track('onboarding_link_copied', { groupId: createdGroupId });
  };

  const handleJoinLink = async () => {
    if (!pasteInput.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const groupMatch = pasteInput.match(/join-group\/([^?#/]+)/);
      const matchMatch = pasteInput.match(/join-match\/([^?#/]+)/);

      if (groupMatch) {
        const token = groupMatch[1];
        const res = await fetch(getApiUrl(`/api/groups/join/${token}`), {
          method: 'POST',
          headers: getAuthHeaders(),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || 'No se pudo unir al grupo');
        }
        const data = await res.json();
        track('onboarding_link_joined', { type: 'group' });
        finish('completed');
        navigate(`/groups/${data.groupId || data.group?.id}`);
      } else if (matchMatch) {
        const matchId = matchMatch[1];
        const res = await fetch(getApiUrl(`/api/matches/${matchId}/join`), {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({}),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || 'No se pudo unir al partido');
        }
        track('onboarding_link_joined', { type: 'match' });
        finish('completed');
        navigate(`/matches/${matchId}`);
      } else {
        throw new Error('El link no es válido. Probá con un link de grupo o partido de Futbolero.');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al procesar el link');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  // Shared styles
  const cardStyle: React.CSSProperties = {
    background: theme.colors.surface,
    borderRadius: 18,
    padding: isMobile ? 24 : 32,
    maxWidth: 440,
    width: '100%',
    border: `1px solid ${theme.colors.border}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    textAlign: 'center',
  };

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: isMobile ? 16 : 24,
  };

  const primaryBtnStyle: React.CSSProperties = {
    width: '100%',
    padding: 14,
    borderRadius: 10,
    border: 'none',
    background: theme.colors.primary,
    color: theme.colors.background,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    opacity: loading ? 0.7 : 1,
  };

  const outlineBtnStyle: React.CSSProperties = {
    width: '100%',
    padding: 14,
    borderRadius: 10,
    border: `1px solid ${theme.colors.border}`,
    background: 'transparent',
    color: theme.colors.text,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 10,
    border: `1px solid ${theme.colors.border}`,
    background: theme.colors.background,
    color: theme.colors.text,
    fontSize: 15,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const backLinkStyle: React.CSSProperties = {
    display: 'inline-block',
    marginBottom: 16,
    color: '#888',
    fontSize: 14,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    textAlign: 'left',
  };

  const titleStyle: React.CSSProperties = {
    margin: '0 0 8px 0',
    color: theme.colors.text,
    fontSize: 22,
    fontWeight: 800,
    textAlign: 'center',
  };

  const subtitleStyle: React.CSSProperties = {
    margin: '0 0 24px 0',
    color: theme.colors.textSecondary,
    fontSize: 15,
    lineHeight: 1.5,
    textAlign: 'center',
  };

  const errorStyle: React.CSSProperties = {
    color: '#e74c3c',
    fontSize: 13,
    marginTop: 8,
    textAlign: 'center',
  };

  // ---- STEP: bifurcation ----
  if (step === 'bifurcation') {
    return (
      <div style={overlayStyle} role="dialog" aria-modal="true">
        <div style={{ ...cardStyle, position: 'relative' }}>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={() => finish('skipped')}
            style={{
              position: 'absolute', top: 10, right: 12,
              background: 'transparent', border: 'none',
              color: theme.colors.textSecondary, fontSize: 26, lineHeight: 1,
              cursor: 'pointer', padding: 4,
            }}
          >
            ×
          </button>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 28,
              background: `${theme.colors.primary}22`,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, marginBottom: 12,
            }}>
              ⚽
            </div>
            <div style={{ color: theme.colors.primary, fontWeight: 800, fontSize: 13, letterSpacing: 2, marginBottom: 6 }}>
              FUTBOLERO
            </div>
            <h2 style={{ ...titleStyle, margin: '0 0 4px 0' }}>¿Cómo vas a usarlo?</h2>
          </div>

          {/* Option 1: Organizer */}
          <div
            onClick={() => setStep('create-group')}
            style={{
              border: `1px solid ${theme.colors.border}`,
              borderRadius: 12,
              padding: 16,
              marginBottom: 12,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'border-color 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = theme.colors.primary)}
            onMouseLeave={e => (e.currentTarget.style.borderColor = theme.colors.border)}
          >
            <div style={{ fontSize: 20, marginBottom: 6 }}>🏆</div>
            <div style={{ color: theme.colors.text, fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
              Soy el que organiza
            </div>
            <div style={{ color: theme.colors.textSecondary, fontSize: 13, marginBottom: 12 }}>
              Creá tu grupo, organizá partidos y compartí el link con tus compañeros.
            </div>
            <button
              type="button"
              onClick={e => { e.stopPropagation(); setStep('create-group'); }}
              style={{ ...primaryBtnStyle, width: 'auto', padding: '10px 20px', fontSize: 14 }}
            >
              Crear mi grupo →
            </button>
          </div>

          {/* Option 2: Player */}
          <div
            onClick={() => setStep('paste-link')}
            style={{
              border: `1px solid ${theme.colors.border}`,
              borderRadius: 12,
              padding: 16,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'border-color 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = theme.colors.primary)}
            onMouseLeave={e => (e.currentTarget.style.borderColor = theme.colors.border)}
          >
            <div style={{ fontSize: 20, marginBottom: 6 }}>👋</div>
            <div style={{ color: theme.colors.text, fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
              Me invitaron a jugar
            </div>
            <div style={{ color: theme.colors.textSecondary, fontSize: 13, marginBottom: 12 }}>
              Tenés un link de grupo o partido. Pegalo para unirte directamente.
            </div>
            <button
              type="button"
              onClick={e => { e.stopPropagation(); setStep('paste-link'); }}
              style={{ ...outlineBtnStyle, width: 'auto', padding: '10px 20px', fontSize: 14 }}
            >
              Pegar mi link →
            </button>
          </div>

          <button
            type="button"
            onClick={() => finish('skipped')}
            style={{
              marginTop: 18,
              background: 'none',
              border: 'none',
              color: theme.colors.textSecondary,
              fontSize: 14,
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Saltar por ahora
          </button>
        </div>
      </div>
    );
  }

  // ---- STEP: create-group ----
  if (step === 'create-group') {
    return (
      <div style={overlayStyle} role="dialog" aria-modal="true">
        <div style={cardStyle}>
          <button type="button" onClick={() => setStep('bifurcation')} style={backLinkStyle}>
            ← Volver
          </button>
          <h2 style={titleStyle}>Creá tu grupo</h2>
          <p style={subtitleStyle}>
            Poné un nombre para tu grupo (ej: "Los del jueves") y empezá a invitar a tus compañeros.
          </p>
          <input
            style={inputStyle}
            type="text"
            placeholder="Nombre del grupo"
            value={groupName}
            onChange={e => setGroupName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !loading && handleCreateGroup()}
            maxLength={60}
            autoFocus
          />
          {error && <div style={errorStyle}>{error}</div>}
          <div style={{ height: 12 }} />
          <button
            type="button"
            onClick={handleCreateGroup}
            disabled={loading || !groupName.trim()}
            style={{ ...primaryBtnStyle, opacity: loading || !groupName.trim() ? 0.6 : 1 }}
          >
            {loading ? 'Creando...' : 'Crear grupo →'}
          </button>
          <div style={{ height: 12 }} />
          <button
            type="button"
            onClick={() => finish('skipped')}
            style={{ background: 'none', border: 'none', color: '#888', fontSize: 14, cursor: 'pointer', padding: 0 }}
          >
            Lo hago después
          </button>
        </div>
      </div>
    );
  }

  // ---- STEP: share-group ----
  if (step === 'share-group') {
    return (
      <div style={overlayStyle} role="dialog" aria-modal="true">
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{
              width: 64, height: 64, borderRadius: 32,
              background: `${theme.colors.primary}22`,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 32, marginBottom: 12,
            }}>
              ✅
            </div>
            <div style={{ color: theme.colors.textSecondary, fontSize: 13, marginBottom: 4 }}>
              {groupName}
            </div>
            <h2 style={titleStyle}>¡Grupo creado!</h2>
            <p style={subtitleStyle}>
              Compartí el link con tus compañeros para que se anoten.
            </p>
          </div>

          {inviteUrl && (
            <div style={{
              background: theme.colors.background,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: 8,
              padding: '10px 14px',
              marginBottom: 16,
              fontSize: 13,
              color: theme.colors.textSecondary,
              wordBreak: 'break-all',
              textAlign: 'left',
            }}>
              {inviteUrl}
            </div>
          )}

          <button
            type="button"
            onClick={handleWhatsApp}
            style={{
              ...primaryBtnStyle,
              background: '#25D366',
              marginBottom: 10,
            }}
          >
            Compartir por WhatsApp
          </button>

          <button
            type="button"
            onClick={handleCopyLink}
            style={{ ...outlineBtnStyle, marginBottom: 16 }}
          >
            Copiar link
          </button>

          <button
            type="button"
            onClick={() => finish('completed')}
            style={{ background: 'none', border: 'none', color: '#888', fontSize: 14, cursor: 'pointer', padding: 0 }}
          >
            Lo hago después →
          </button>
        </div>
      </div>
    );
  }

  // ---- STEP: paste-link ----
  return (
    <div style={overlayStyle} role="dialog" aria-modal="true">
      <div style={cardStyle}>
        <button type="button" onClick={() => setStep('bifurcation')} style={backLinkStyle}>
          ← Volver
        </button>
        <h2 style={titleStyle}>Pegá tu link</h2>
        <p style={subtitleStyle}>
          Copiá el link que te mandaron y pegalo acá para unirte al grupo o partido.
        </p>
        <input
          style={inputStyle}
          type="url"
          placeholder="https://futbolero.ar/join-group/..."
          value={pasteInput}
          onChange={e => setPasteInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !loading && handleJoinLink()}
          autoFocus
        />
        {error && <div style={errorStyle}>{error}</div>}
        <div style={{ height: 12 }} />
        <button
          type="button"
          onClick={handleJoinLink}
          disabled={loading || !pasteInput.trim()}
          style={{ ...primaryBtnStyle, opacity: loading || !pasteInput.trim() ? 0.6 : 1 }}
        >
          {loading ? 'Uniéndome...' : 'Unirme →'}
        </button>
        <div style={{ height: 16 }} />
        <div style={{ color: '#888', fontSize: 13, textAlign: 'center' }}>
          ¿No tenés link? Pedíselo al organizador de tu grupo.
        </div>
      </div>
    </div>
  );
};
